import { useEffect } from "react";
import { useBlocker, useNavigate } from "react-router";

import {
  cancelSetup,
  discardSession,
  peak,
  stopRecording,
  toggleChirp,
} from "../engine/session";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { durationWords } from "../engine/duration-words";
import { uploadHealthOf, useApp, type UploadHealth } from "../state/store";
import {
  BroadcastIcon,
  Button,
  Card,
  CodeCard,
  DetailsPanel,
  fmtClockPad,
  Hint,
  LevelBars,
  MicIcon,
  NotifyOffer,
  ProgressBar,
  StatusLine,
  StopCircleIcon,
  cx,
} from "../ui";
import { Screen } from "./screen";
import { sayOn, useScreenStatus } from "./use-screen-status";

/** What the upload line says, and in which colour. */
const UPLOAD_HEALTH: Record<UploadHealth, { label: string; className: string }> = {
  uploaded: { label: t("session.upload_uploaded"), className: "text-ok" },
  catching_up: { label: t("session.upload_catching_up"), className: "text-warn-ink" },
  reconnecting: { label: t("session.upload_reconnecting"), className: "text-warn-ink" },
  // Plain and true: nothing after this point reaches the server, and nothing will retry.
  refused: { label: t("session.upload_refused"), className: "text-danger" },
};

/**
 * The clock plate's one line: setting up, a meeting in progress, or ended.
 *
 * Setting up is the stretch between Start or Join and this device's first captured audio
 * (#658). Nothing is recorded yet, so nothing on the plate may look like a meeting.
 */
export function meetingHeadline(recording: boolean, settingUp = false): string {
  if (settingUp) return t("session.setting_up");
  return recording
    ? t("session.meeting_in_progress")
    : t("session.meeting_ended");
}

/**
 * Whether the clock plate is shown and counting.
 *
 * The plate belongs to the meeting screen, so it stays through the wrap-up and
 * after the host ends it -- a frozen record of the meeting, the same in
 * `mf-present` (`clock_visible`). Only the counting stops, at the moment the
 * microphone closes (`clock_running`), so a wrap-up can never look live.
 */
export function meetingClock(recording: boolean): { visible: boolean; running: boolean } {
  return { visible: true, running: recording };
}

/**
 * The wrap card's one sentence, in the three plain statuses the screen shows.
 *
 * The shared present fixture's wrap row carries the same message key; the web
 * test compares this real sentence to it (`#493`).
 */
export function wrapMessage(wrap: { key: string }): string {
  if (wrap.key === "waiting") return t("session.waiting_end");
  if (wrap.key === "merge") return t("session.preparing_notes");
  return t("session.saving_part");
}

/**
 * The persistent banner for a capture stall, in whole seconds read out in words.
 * A stall under a whole second rounds to nothing worth telling, so this returns
 * `null` and the screen shows no notice (`#988`).
 */
export function capturePauseMessage(gapSec: number): string | null {
  const seconds = Math.round(gapSec);
  if (seconds < 1) return null;
  return t("session.capture_pause", { duration: durationWords(seconds) });
}

/**
 * The session screen: record now, read later.
 *
 * Reads only; every decision is the engine's. While recording it plates what a person at
 * the table reads from arm's length -- the clock, the code, the devices in the room with
 * this one's level and upload health -- with the controls under them in thumb's reach.
 * There is no transcript here: the meeting is processed on the server after Stop, and
 * the screen then says so and lets the person go. The level meter is the one thing that
 * does not come through the store -- it pulls `peak()` on its own animation frame,
 * because a value repainted 60 times a second has no business being React state.
 */
export function Session() {
  const navigate = useNavigate();
  const [status] = useScreenStatus();
  const session = useApp((s) => s.session);
  const phase = useApp((s) => s.phase);
  const recording = useApp((s) => s.recording);
  const settingUp = useApp((s) => s.settingUp);
  const stopping = useApp((s) => s.stopping);
  const reported = useApp((s) => s.reported);
  const elapsed = useApp((s) => s.elapsedSec);
  const capturePause = useApp((s) => s.capturePause);
  const isCreator = useApp((s) => s.isCreator);
  const chirping = useApp((s) => s.chirping);
  const wrap = useApp((s) => s.wrap);
  const resultSessionId = useApp((s) => s.resultSessionId);
  const roster = useApp((s) => s.roster);
  const credentials = useApp((s) => s.credentials);
  const account = useApp((s) => s.account);
  const uplinkState = useApp((s) => s.streamUplinkState);
  const uplinkHealth = useApp((s) => s.uplinkHealth);
  const uplinkRefused = useApp((s) => s.uplinkRefused);
  const processing = phase === "processing";
  // A stall under a whole second has no notice to show (#988).
  const pauseMessage = capturePause ? capturePauseMessage(capturePause.gapSec) : null;
  const upload = UPLOAD_HEALTH[uploadHealthOf(uplinkState, uplinkHealth, uplinkRefused)];
  const devices = devicesIn(
    roster ?? {},
    account?.participant_id ?? null,
    (credentials?.displayName as string | undefined) ?? null
  );

  /**
   * Leaving with the microphone open loses the conversation.
   *
   * The recording lives only in this page's memory and the outbox holds bundles that have
   * been scored and signed but not yet accepted; navigating away drops both. `beforeunload`
   * covers closing the tab and the engine installs it; this covers the in-app equivalent,
   * which `beforeunload` never sees.
   */
  // The same window the update flow guards, and for the same reason: wrap-up is where the
  // un-posted claims and the tail audio live, and leaving takes them with it.
  //
  // Setup is in the window too, but the other way round (#658): nothing is recorded yet, so
  // leaving is allowed and is a Cancel -- the microphone closes and the session is left,
  // rather than the person being held on a screen they asked to leave.
  const blocker = useBlocker(() => recording || stopping || settingUp);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (useApp.getState().settingUp) {
      cancelSetup();
      blocker.proceed?.();
      return;
    }
    sayOn(
      "/session",
      t(useApp.getState().isCreator ? "session.blocker_host" : "session.blocker"),
      "warn"
    );
    blocker.reset?.();
  }, [blocker]);

  /**
   * The engine cannot navigate; it moves the phase and this reacts.
   *
   * `replace`, in all three cases, and that is what keeps the back button out of a dead
   * session. This screen is finished by the time any of them fire -- the recording is over
   * and the code is spent -- so leaving it in history gives Back somewhere to land that can
   * only bounce forward again. [V] It did exactly that: Back off the result screen returned
   * to `/session`, whose effect saw `phase === "done"` and navigated to the result a second
   * time. Replacing the entry means Back from the result goes to whatever came before the
   * session, which is home -- where the next one starts.
   */
  useEffect(() => {
    if (phase === "done" && resultSessionId) {
      navigate(`/session/${resultSessionId}/result`, { replace: true });
    }
    if (phase === "left") navigate("/", { replace: true });
    if (phase === "revoked") navigate("/enroll", { replace: true });
  }, [phase, resultSessionId, navigate]);


  return (
    <Screen name="session">
      {/* The clock plate. While the microphone is open this *is* the state of the world:
          a red pulse and the seconds since the first sample, read across a table. */}
      <Card
        bare
        className="relative overflow-hidden my-4 flex flex-col items-center py-plate bg-surface-2 border-line"
      >
        <span aria-hidden="true" className="absolute inset-0 pointer-events-none scanlines" />
        <p className="relative m-0 mb-tight flex items-center gap-nudge font-code text-label font-semibold uppercase tracking-label text-ink-soft">
          {recording ? (
            <>
              <span
                className="size-dot rounded-full bg-danger animate-pulse motion-reduce:animate-none flex-none"
                aria-hidden="true"
              />
              {meetingHeadline(true)}
            </>
          ) : (
            <>
              <span className="size-dot rounded-full bg-line-strong flex-none" aria-hidden="true" />
              {meetingHeadline(false, settingUp)}
            </>
          )}
        </p>
        {/* Minutes padded so the digits hold their columns: a clock that shifts width
            every ten minutes is a clock somebody has to re-read. */}
        <div className="relative font-code text-code font-bold tracking-code retrack-code tabular-nums">
          {fmtClockPad(elapsed)}
        </div>
      </Card>

      {/* The boarding pass. The note says what the code does because the person holding
          this device is the one who has to decide to read it out. */}
      {/* After this device's wrap-up: the server has the meeting. Said plainly, with the
          stage when the server names one, and a way out -- nothing here needs the person
          to stay, because the result arrives by email. */}
      {processing ? (
        <Card className="grid gap-3" aria-live="polite" aria-label={t("session.processing_aria")}>
          <span className="font-title text-person font-semibold tracking-headline text-ink">
            {t("session.preparing_notes")}
          </span>
          <p className="m-0 text-note text-ink">
            {t("session.email_when_ready")}
          </p>
          <NotifyOffer />
          <Button variant="ghost" onClick={() => navigate("/", { replace: true })}>
            {t("common.done")}
          </Button>
        </Card>
      ) : null}

      {processing ? null : (
        <>
          <CodeCard
            code={(session?.join_code as string) ?? t("session.code_placeholder")}
            note={t("session.share_code")}
          />

          {/* The devices in the room. This one carries its live level and whether its
              audio has reached the server; the others are listed by name, because
              their levels and uploads live on their own devices. */}
          <Card bare className="my-4 p-4 bg-surface-2 border-line" aria-label={t("session.participants_aria")}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="min-w-0 truncate font-code text-label font-semibold uppercase tracking-label text-ink">
                {devices.own}
                <span className="text-ink-soft"> · {t("session.this_device")}</span>
              </span>
              <MicIcon className="size-icon-xs text-accent/60 flex-none" />
            </div>
            <LevelBars variant="field" read={peak} active={recording} className="h-24" />
            {recording ? (
              <p
                className={cx("m-0 mt-2 font-code text-count uppercase tracking-label", upload.className)}
                aria-label={t("session.upload_health_aria")}
              >
                {upload.label}
              </p>
            ) : null}
            {devices.others.length ? (
              <ul className="list-none m-0 mt-4 p-0 grid gap-tight border-t border-line pt-stack">
                {devices.others.map((name, i) => (
                  <li
                    key={`${name}-${i}`}
                    className="flex items-center justify-between gap-2 font-code text-label uppercase tracking-label"
                  >
                    <span className="min-w-0 truncate text-ink">{name}</span>
                    <span className="flex-none text-ink-soft">{t("session.in_meeting")}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        </>
      )}

      {/* A stall banner, not a toast: whoever backgrounded the tab was not looking
          at it when the stall happened, so a transient message would report the
          hole to nobody. Persists until stop or a fresh recording; the store
          owns the lifetime, this only renders it. */}
      {recording && !wrap && pauseMessage ? (
        <Hint aria-label={t("session.capture_pause_aria")}>{pauseMessage}</Hint>
      ) : null}

      {/* The beacon. A switch, because leaving it on for the whole session is a
          legitimate choice rather than a momentary action -- the row's job is to show
          which way it points, not to beg to be turned off. Not offered while setting up:
          the beacon starts with the meeting (#658). */}
      {processing || settingUp ? null : (
      <button
        type="button"
        onClick={() => {
          captureAnalytics("meeting_nearby_toggled", { state: chirping ? "off" : "on" });
          toggleChirp();
        }}
        aria-pressed={chirping}
        className={cx(
          "my-4 w-full flex items-center justify-between gap-4 p-panel border cursor-pointer",
          "button-transition enabled:active:button-press motion-reduce:enabled:active:transform-none",
          chirping ? "border-accent stage-live-tint" : "border-line-strong bg-surface"
        )}
      >
        <span className="flex items-center gap-4 min-w-0">
          <span className="relative flex items-center justify-center size-tile-md flex-none">
            {/* The ring fires only while broadcasting, and only for people who have not
                asked their device to hold still. */}
            {chirping ? (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-accent/20 animate-ping motion-reduce:animate-none"
              />
            ) : null}
            <span
              className={cx(
                "relative z-10 size-icon-xl rounded-full flex items-center justify-center border",
                chirping
                  ? "bg-accent text-accent-ink border-transparent"
                  : "bg-surface-2 text-ink-soft border-line"
              )}
            >
              <BroadcastIcon className="size-icon-xs" />
            </span>
          </span>
          <span className="font-code text-button font-semibold uppercase tracking-label text-left">
            {t(chirping ? "session.nearby_on" : "session.let_nearby_join")}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cx(
            "relative w-12 h-6 rounded-full p-1 flex-none transition-colors ease-plain motion-reduce:transition-none",
            chirping ? "bg-accent/30" : "bg-surface-3"
          )}
        >
          <span
            className={cx(
              "block size-icon-xs rounded-full transition-transform ease-plain motion-reduce:transition-none",
              chirping ? "translate-x-6 bg-accent" : "translate-x-0 bg-ink-soft"
            )}
          />
        </span>
      </button>
      )}

      {!processing && !settingUp ? <DetailsPanel summary={t("session.nearby_details")}><p>{t("session.nearby_access")}</p></DetailsPanel> : null}

      {/* What happens after Stop takes 10-30 s on a slow device, most of it one ECAPA pass on
          the device itself. A status line alone read as a frozen button, so the stages are listed
          and the scoring one carries a real percentage. */}
      {wrap ? (
        <Card className="grid gap-3" aria-live="polite">
          <ProgressBar
            fraction={wrap.fraction ?? 0}
            indeterminate={wrap.fraction === null}
            className="w-full"
          />
          <p className="m-0 text-note">
            {wrapMessage(wrap)}
          </p>
          {/* Below the stages, not above them: the progress is what somebody
              opened this screen to see, and the offer is only worth making
              *because* that progress can outlast their attention. Renders
              nothing when push is unavailable or already declined. */}
          <NotifyOffer />
        </Card>
      ) : null}

      {/* Setting up (#658): nothing is recorded yet, so there is nothing to end or discard,
          only a start to call off. */}
      {settingUp ? (
        <div className="grid gap-row mt-4">
          <Button variant="ghost" onClick={() => {
            captureAnalytics("meeting_ended", { outcome: "setup_cancelled" });
            cancelSetup();
          }}>
            {t("common.cancel")}
          </Button>
        </div>
      ) : null}

      {processing || settingUp ? null : (
      <div className="grid gap-row mt-4">
        {/* Disabled by the latch that wrap-up actually holds, not by the phase. [V] The
            phase was never reset, so a wrap-up that threw left this dead for good beside an
            error inviting a second press. Outlined in the danger colour rather than filled:
            ending things should be deliberate, and a filled red slab invites exactly the
            reflex tap this button exists to prevent. */}
        <Button
          variant="ghost"
          danger
          disabled={stopping}
          onClick={() => {
            captureAnalytics("meeting_ended", { outcome: isCreator ? "stopped" : "left" });
            void stopRecording();
          }}
          className="flex items-center justify-center gap-nudge"
        >
          <StopCircleIcon className="size-icon" />
          {reported
            ? t("session.get_summary")
            : isCreator
              ? t("session.end_for_everyone")
              : t("session.leave_meeting")}
        </Button>
        {/* Audio reaches the server *during* the session, so "I changed my mind" needs
            somewhere to go. Never for the creator: a host has no part to discard, since
            only their End closes the meeting -- the server refuses it too. Hidden while
            wrapping up: by then the recording is already on its way and the card above
            is the thing to watch. */}
        {wrap || isCreator ? null : (
          <Button
            variant="ghost"
            onClick={async () => {
              captureAnalytics("meeting_ended", { outcome: "discarded" });
              const note = await discardSession();
              if (note) sayOn("/", note);
            }}
          >
            {t("session.discard")}
          </Button>
        )}
      </div>
      )}

      {/* The label has to be honest about the difference. One of these ends the
          conversation for everybody at the table; the other only bows out of it. */}
      {processing || settingUp || isCreator ? null : (
        <Hint>
          {t("session.end_hint_guest")}
        </Hint>
      )}

      <StatusLine kind={status.kind}>{status.message}</StatusLine>
    </Screen>
  );
}

/**
 * This device's name and the others in the room, from the session roster.
 *
 * This device is the roster entry for the signed-in account's participant id. When that
 * id is not known yet, the first entry carrying this device's own name stands in for it,
 * so the device is not listed twice.
 */
export function devicesIn(
  roster: Record<string, string>,
  ownParticipantId: string | null,
  ownDisplayName: string | null
): { own: string; others: string[] } {
  const own =
    ownDisplayName?.trim() || (ownParticipantId ? roster[ownParticipantId] : undefined) || t("session.this_device_name");
  const entries = Object.entries(roster).filter(([id]) => id !== ownParticipantId);
  if (!ownParticipantId || !(ownParticipantId in roster)) {
    const index = entries.findIndex(([, name]) => name === own);
    if (index >= 0) entries.splice(index, 1);
  }
  return { own, others: entries.map(([, name]) => name) };
}
